import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import * as turf from '@turf/turf';

import { TilesetsQuery } from '../../queries';
import suitabilityAcrossLayersAndRegions from '../../utils/webgl/suitability-calculations/suitabilityAcrossLayersAndRegions';
import {
  CHINESE_CHESTNUT_TILESET_NAME,
  BLACK_LOCUST_TILESET_NAME,
  HYBRID_POPLAR_TILESET_NAME,
  BLACKCURRANT_TILESET_NAME,
  AMERICAN_ELDERBERRY_TILESET_NAME,
  AMERICAN_HAZELNUT_TILESET_NAME,
} from '../../constants/tilesets';
import { layerIdForTileset } from '../../utils/tilesets';

const TILESET_IDS_TO_USE = [
  CHINESE_CHESTNUT_TILESET_NAME,
  BLACK_LOCUST_TILESET_NAME,
  HYBRID_POPLAR_TILESET_NAME,
  BLACKCURRANT_TILESET_NAME,
  AMERICAN_ELDERBERRY_TILESET_NAME,
  AMERICAN_HAZELNUT_TILESET_NAME,
];

// Only report progress updates every 5 iterations (and on the last one)
// This improves performance a bit.
const PROGRESS_FREQ = 5;

export const getCenterPoint = (feature) => {
  const center = turf.centerOfMass(feature);
  center.id = feature.id;
  center.properties.id = feature.id;
  return center;
};

/**
 * Fits mapbox `map` to the bounds of the given `feature` with padding around the edges corresponding to
 * `paddingProportion` of the map's viewport width and height.
 */
export const fitMapToFeature = (map, feature, paddingProportion = 0.25) => {
  const padding = Math.min(
    map.transform.width * paddingProportion,
    map.transform.height * paddingProportion,
  );
  const bbox = turf.bbox(feature);
  map.fitBounds(bbox, {
    padding,
    animate: false,
  });
};

/**
 * Fetches tileset metadata and manages selected crops.
 *
 * Returns:
 * - `tilesets`: Tileset data for the selected crops
 * - `cropOptions`: Options for the crop select dropdown ({ value, label }, from tileset metadata query)
 * - `selectedCrops`: The currently selected crops (array of values that match `tileset.name`)
 * - `setSelectedCrops`: Function to set the selected crops
 */
export const useTilesets = () => {
  // These match the tileset.name values
  const [selectedCrops, setSelectedCrops] = useState([]);

  const { data, error } = useQuery(TilesetsQuery, {
    fetchPolicy: 'cache-and-network',
  });
  if (error) console.error(error);

  const fetchedTilesets = data?.tilesets;
  const allTilesets = useMemo(
    () =>
      (fetchedTilesets ?? []).map((t) => ({
        ...t,
        /**
         * NOTE: Each tileset has one or more values in its `displayNames` array.
         * For our crop suitability tilesets, the first value is the crop name, and
         * the second value is something like "Present Day", "In 20 Years", etc. For
         * other tilesets, there's just one value in the array, the name. Given that
         * we're only interested in the name (and we're not using anything that's
         * not present day data), we just take the first value here:
         */
        displayName: t.displayNames[0],
      })),
    [fetchedTilesets],
  );

  const cropOptions = useMemo(
    () =>
      allTilesets
        .filter((t) => TILESET_IDS_TO_USE.includes(t.name))
        .map((t) => ({ value: t.name, label: t.displayName })),
    [allTilesets],
  );

  const tilesets = useMemo(
    () => allTilesets.filter((t) => selectedCrops.includes(t.name)),
    [selectedCrops, allTilesets],
  );

  return {
    tilesets,
    cropOptions,
    selectedCrops,
    setSelectedCrops,
  };
};

/**
 * Returns `{calculateSuitability, loadingState}`, where
 * - `calculateSuitability` is a function that can be used to calculate suitability for the current set of features & layers, and
 * - `loadingState` is an object that represents the current loading state. It will be one of:
 *   - null = not loading
 *   - { status: 'preloading', progress: [N: int, M: int] } = preloading tile N out of M
 *   - { status: 'calculating', progress: [N: int, M: int] } = calculating suitability for feature N out of M
 */
export const useCalculateSuitability = (map, features, tilesets) => {
  const [loadingState, setLoadingState] = useState(null);

  const layerIds = useMemo(
    () => tilesets.map((tileset) => layerIdForTileset(tileset.id)),
    [tilesets],
  );

  const featureCollections = useMemo(
    () => features.map((f) => turf.featureCollection([f])),
    [features],
  );
  const calculateSuitability = async () => {
    try {
      return await suitabilityAcrossLayersAndRegions({
        map,
        layerIds,
        featureCollections,
        onProgress: (status, item, total) => {
          setLoadingState({ status, progress: [item, total] });
        },
        progressFreq: PROGRESS_FREQ,
      });
    } finally {
      setLoadingState(null);
    }
  };

  return {
    calculateSuitability,
    loadingState,
  };
};
